import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FieldValues, useForm, UseFormGetValues } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { ParticipantMainForm } from './ParticipantMainForm'
import { ParticipantChildForm } from './ParticipantChildForm'
import { ParticipantCodeLoading } from './ParticipantCodeModal'
import { ParticipantSiblingForm } from './ParticipantSiblingForm'
import { RootState } from '../../store'
import { addToast } from '../../store/misc/misc'
import { IChild, ISibling } from '../../store/participant/interfaces'
import { ToastType } from '../../store/misc/interfaces'
import StageNavigation from '../shared/StageNavigation'
import * as routes from '../../routes'
import { setParticipant, setParticipantChildren, setParticipantSibling } from '../../store/participant/participant'
import { CustomAnswer, CustomQuestion } from '../../store/events/interfaces'
import { fieldRequired, mainSchema } from './ParticipantSchemaDefinitions'
import useValidators from '../../hooks/validators'
import { setPaymentMethod } from '../../store/paymentData'
import { checkProperAge } from '../../functions/registrations/ageIntegrity'
import { checkProperGender } from '../../functions/registrations/genderIntegrity'
import { IInstitution } from '../../store/institution/interfaces'
import { useAppSelector } from '../../hooks'
import checkPaymentStageSkip from '../../functions/registrations/checkPaymentStageSkip'

type FormValuesMap = Map<number, UseFormGetValues<FieldValues>>

const ParticipantStage = () => {
  let navigate = useNavigate()
  const dispatch = useDispatch()
  const { addValidator, removeValidator, hasValidationErrors } = useValidators()
  const { data: program } = useAppSelector((state: RootState) => state.program)
  const { list: registrations } = useAppSelector(
    (state: RootState) => state.registrations,
  )
  const { data: institution } = useAppSelector(
    (state: RootState) => state.institution,
  )
  const [subFormValues, setSubFormValues] = useState<FormValuesMap>(new Map())

  const initialChildren = useAppSelector((state) => state.participant.data.children)
  const initialSiblings = useAppSelector((state) => state.participant.data.siblings)

  const [children, setChildren] = useState<IChild[]>(initialChildren || [])
  const [siblings, setSiblings] = useState<ISibling[]>(initialSiblings || [])

  program.participant_questions.forEach((question: CustomQuestion) => {
    mainSchema[`custom_answer$${question.id}`] = question.required
      ? yup.string().required(fieldRequired)
      : yup.string()
  })
  const { data: participant } = useAppSelector(
    (state: RootState) => state.participant,
  )
  useEffect(() => {
    // Update the document title using the browser API
    document.title = `${program.name}: Persönliche Angaben | Feripro ${institution.name}`
  }, [institution.name, program.name])

  const isOnlyAdultEvents = () => {
    for (const reg of registrations) {
      if (!reg.event.is_only_for_adults) {
        return false
      }
    }
    return true
  }

  const newSibling: ISibling = {
    id: siblings.length ? siblings[siblings.length - 1].id + 1 : 1,
    first_name: '',
    gender: '',
    date_of_birth: '',
    disabled: false,
    handicap: '',
  }
  const createNewChild = () => {
    console.log('Creating new child!', children)
    return {
      id: children.length ? children[children.length - 1].id + 1 : 1,
      name: '',
      date_of_birth: '',
    } as IChild
  }

  const [skipPaymentStage, setSkipPaymentStage] = useState(
    checkPaymentStageSkip(program, registrations),
  )

  const getSendingPostCheck = (institution: IInstitution) => {
    let sendingPostTest = (target: string) => {
      return true
    }
    if (
      'participant-stage' in institution.custom_configuration &&
      'behavior' in institution.custom_configuration['participant-stage'] &&
      institution.custom_configuration['participant-stage']['behavior'] ===
      'custom-school-select'
    ) {
      sendingPostTest = (target: string) => {
        const receivers =
          institution.custom_configuration['participant-stage']['data'][
            'receivers'
            ]
        return receivers.some(
          (sending_post: { name: string; email: string; address: string }) =>
            sending_post.name === target,
        )
      }
    }
    return mainSchema.sending_post.test(
      'validChoices',
      'Bitte wählen Sie eine der gegebenen Optionen aus.',
      sendingPostTest,
    )
  }

  const validationSchema = yup.object().shape({
    ...mainSchema,
    gender: mainSchema.gender.test({
      name: 'proper_gender_check',
      test: function(value: string) {
        const res = checkProperGender(value, registrations)
        return res.ok && !res.message
          ? true
          : this.createError({ path: 'gender', message: res.message })
      },
    }),
    date_of_birth: mainSchema.date_of_birth.test({
      name: 'proper_age_check',
      test: function(value: string) {
        if (program.requires_family_data) {
          // Skip age checking on the parent in family registration
          return true
        }
        const res = checkProperAge(
          value,
          registrations,
          program.age_check_tolerance,
        )
        return res.ok && !res.message
          ? true
          : this.createError({ path: 'date_of_birth', message: res.message })
      },
    }),
    zip_code: mainSchema.zip_code
      .test({
        name: 'custom_module_fulda_check',
        test: function(zip: string) {
          const setZipCode =
            'participant-stage' in institution.custom_configuration &&
            'behavior' in
            institution.custom_configuration['participant-stage'] &&
            institution.custom_configuration['participant-stage'][
              'behavior'
              ] === 'validate-zipcode-with-categories'
          if (setZipCode) {
            const mapping =
              institution.custom_configuration['participant-stage']['data'][
                'mapping'
                ]
            const requiredCategory =
              zip in mapping ? mapping[zip] : mapping['*']
            const blockingEvents: string[] = []
            for (const reg of registrations) {
              if (reg.event.price_categories.length > 0) {
                const category = reg.event.price_categories.find(
                  price_cat => price_cat.name === requiredCategory,
                )
                if (
                  category &&
                  !category.has_free_places &&
                  category.is_waiting_list_full
                ) {
                  blockingEvents.push(`"${reg.event.name}"`)
                }
              }
            }
            if (blockingEvents.length > 0) {
              let message: string
              blockingEvents.length === 1
                ? (message = `In der Veranstaltung ${blockingEvents[0]} ist für Ihre Postleitzahl (Kategorie "${requiredCategory}") kein Platz mehr frei.`)
                : (message = `In den Veranstaltungen ${blockingEvents.join(
                  ', ',
                )} sind für Ihre Postleitzahl (Kategorie "${requiredCategory}") keine Plätze mehr frei.`)

              return this.createError({
                path: 'zip_code',
                message: message,
              })
            }
          }
          return true
        },
      })
      .test(
        'validChoices',
        'Bitte wählen Sie eine der gegebenen Optionen aus.',
        (val: string) =>
          !program.restrict_zip_codes ||
          program.restricted_zip_codes.some(zipcode => zipcode === val),
      ),
    sending_post: getSendingPostCheck(institution),
  })
  const { register, formState, formState: { errors }, getValues, setValue, trigger, reset } = useForm(
    {
      resolver: yupResolver(validationSchema),
      mode: 'onBlur',
    },
  )

  useEffect(() => {
    addValidator('participant', { trigger, formName: 'ParticipantForm' })
  }, [addValidator, trigger])

  // Works like default values -> reset to data from participant
  useEffect(() => {
    reset({ ...participant, email_repeat: participant.email })
  }, [participant])


  const processForms = async () => {

    const childFormValueList = await Promise.all(
      Array.from(subFormValues.values()).map(async gV => await gV()),
    )

    const min_children_amount = Math.max(
      ...registrations.map(reg => reg.numberOfChildren),
    )

    if (
      program.requires_family_data &&
      childFormValueList.length < min_children_amount
    ) {
      dispatch(
        addToast({
          body: `Sie müssen mindestens ${min_children_amount} Kind${min_children_amount >= 2 ? 'er' : ''} angeben`,
          title: 'Formularfehler',
          type: ToastType.Danger,
        }),
      )
      return false
    }
    const validationErrors = await hasValidationErrors()
    if (Object.keys(errors).length > 0 || validationErrors) {
      console.table(errors)
      dispatch(
        addToast({
          body: 'Das Formular enthält noch Fehler.',
          title: 'Formularfehler',
          type: ToastType.Danger,
        }),
      )
      return false
    } else {
      const newParticipant = getValues()
      if (newParticipant.sending_post) {
        newParticipant.school = newParticipant.sending_post
      }

      program.ask_for_photo_permission &&
      (newParticipant.photos_allowed = false)

      program.agreement_html && (newParticipant.newsletter_allowed = false)

      const customAnswers: CustomAnswer[] = []
      const answers: string[] = Object.keys(
        newParticipant,
      ).filter((key: string) => key.startsWith('custom_answer$'))

      answers.forEach(answer => {
        const id: string = answer.split('$')[1]
        const customQuestion: CustomQuestion = program.participant_questions.find(
          question => parseInt(id) === question.id,
        ) as CustomQuestion
        if (customQuestion) {
          customAnswers.push({
            question_id: parseInt(id),
            question_label: customQuestion.label,
            question_type: customQuestion.type,
            question_description: customQuestion.description,
            answer_required: customQuestion.required,
            answer_choices: customQuestion.choices,
            answer_text: newParticipant[answer],
            question_scope: customQuestion.scope,
          })
        }
      })
      newParticipant.custom_answers = customAnswers
      newParticipant.token = participant.token
      newParticipant.siblings = participant.siblings
      newParticipant.children = participant.children

      dispatch(setParticipant(newParticipant))
      const updatedValueList = childFormValueList.filter(
        list => Object.keys(list).length > 1,
      ).map((values, index) => {
        return { id: index, ...values }
      })
      // Only one of these can be active at any time. If both are off, the formValueList is empty
      if (childFormValueList.length !== 0) {
        console.log('updatedValueList:', updatedValueList)
        dispatch(
          program.is_siblings_registration_enabled
            ? setParticipantSibling(updatedValueList)
            : setParticipantChildren(updatedValueList),
        )
      }
      return true
    }
  }

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>
            <h2 aria-level={1}>
              Persönliche Angaben
            </h2>
          </Card.Title>
          <Card.Text>
            {program.is_siblings_registration_enabled ? (
              <>
                Geschwisterkinder können durch Klicken auf &ldquo;Weiteres
                Geschwisterkind hinzufügen&rdquo; hinzugefügt werden.
                <br />
                Bitte beachten: Alle Geschwisterkinder werden auf alle
                ausgewählten Veranstaltungen angemeldet. Sollten Sie mehr
                Geschwisterkinder anmelden als freie Teilnehmerplätze verfügbar
                sind, werden nicht alle einen Teilnehmerplatz erhalten.
              </>
            ) : (
              <>
                Mehrere Teilnehmer (auch Geschwister) müssen separat angemeldet
                werden. Dies garantiert, dass möglichst viele Veranstaltungen
                besucht werden können.
              </>
            )}
          </Card.Text>
          <Card.Text>Notwendige Felder sind mit einem * markiert.</Card.Text>
        </Card.Body>
        {program.access_key_mode !== 'off' &&
          program.loading_via_access_keys_possible && (
            <Card.Footer>
              <ParticipantCodeLoading />
            </Card.Footer>
          )}
      </Card>
      <Card className='mt-3'>
        <Card.Body>
          <Row>
            <Col
              md={
                (program.requires_family_data ||
                program.is_siblings_registration_enabled) && !isOnlyAdultEvents()
                  ? 6
                  : 12
              }
              sm={12}
            >
              <ParticipantMainForm
                register={register}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                formState={formState}
                participant={participant}
                updatePaymentPageSkip={() =>
                  setSkipPaymentStage
                }                
              />
            </Col>
            {program.requires_family_data &&
              !program.is_siblings_registration_enabled && !isOnlyAdultEvents() && (
                <Col md={6} sm={12}>
                  <h5>Daten Ihrer Kinder ({children.length})</h5>
                  <Button
                    variant='primary'
                    onClick={() => setChildren([...children, createNewChild()])}
                    className='btn mobile-btn mb-2'
                  >
                    Kind hinzufügen
                  </Button>
                  {children ? (
                    children.map(child => {
                      return (
                        <div key={child.id}>
                          <ParticipantChildForm
                            child={child}
                            onCreateForm={
                              (formValue, validator) => {
                                setSubFormValues(formValues => {
                                  formValues.set(child.id, formValue)
                                  return formValues
                                })
                                addValidator(`child-${child.id}`, validator)
                              }
                            }
                            onRemoveForm={
                              () => {
                                // delete from child form values map
                                setSubFormValues(formValues => {
                                  formValues.delete(child.id)
                                  return formValues
                                })
                                // remove the validators
                                removeValidator(`child-${child.id}`)
                                // delete from local state
                                setChildren(
                                  children.filter((c) => c.id !== child.id),
                                )
                              }
                            }
                          />
                          <br />
                        </div>
                      )
                    })
                  ) : (
                    <></>
                  )}
                </Col>
              )}
            {program.is_siblings_registration_enabled &&
              !program.requires_family_data && (
                <Col md={6} sm={12}>
                  <h5>Geschwisterkinder</h5>
                  {siblings ? (
                    siblings.map(sibling => {
                      return (
                        <div key={sibling.id}>
                          <ParticipantSiblingForm
                            sibling={sibling}
                            onCreateForm={(formValue, validator) => {
                              setSubFormValues(formValues => {
                                formValues.set(sibling.id, formValue)
                                return formValues
                              })
                              addValidator(`sibling-${sibling.id}`, validator)
                            }
                            }
                            onRemoveForm={
                              () => {
                                setSubFormValues(formValues => {
                                  formValues.delete(sibling.id)
                                  return formValues
                                })
                                removeValidator(`sibling-${sibling.id}`)
                                setSiblings(
                                  siblings.filter((s) => s.id !== sibling.id),
                                )
                              }
                            }
                          />
                          <br />
                        </div>
                      )
                    })
                  ) : (
                    <></>
                  )}
                  <Button
                    variant='primary'
                    onClick={() => setSiblings([...siblings, newSibling])}
                    className='btn mobile-btn'
                  >
                    Geschwisterkind hinzufügen
                  </Button>
                </Col>
              )}
          </Row>
        </Card.Body>
      </Card>
      <StageNavigation
        rerender={skipPaymentStage}
        backward={{
          text: 'Zurück',
          handler: () => navigate(routes.REGISTRATIONS.path),
        }}
        forward={
          skipPaymentStage
            ? {
              text: routes.VERIFY.longName,
              handler: () =>
                processForms().then(redirect => {
                  if (redirect) {
                    dispatch(setPaymentMethod('free'))
                    navigate(routes.VERIFY.path)
                  }
                }),
            }
            : {
              text: routes.PAYMENT.longName,
              handler: () =>
                processForms().then(redirect => {
                  if (redirect) {
                    navigate(routes.PAYMENT.path)
                  }
                }),
            }
        }
      />
    </>
  )
}

export default ParticipantStage
